import { Alert, Modal, Space, Typography } from "@arco-design/web-react";
import { useEffect, useState } from "react";

import {
  FULFILLMENT_ARCHIVE_ACCEPT,
  getSafeFulfillmentOrderActionErrorMessage,
  isFulfillmentOrderStaleActionError,
  validateArchiveFile,
} from "../service/supplierConfirmationService";

type Props = {
  visible: boolean;
  loading?: boolean;
  title?: string;
  effectiveOnUpload?: boolean;
  onCancel: () => void;
  onSubmit: (file: File) => Promise<unknown>;
};

/** 上传 PDF、PNG 或 JPEG 盖章归档，提交前执行 20 MiB 客户端校验。 */
export function FulfillmentOrderArchiveUploadModal({
  visible,
  loading = false,
  title = "上传盖章文件",
  effectiveOnUpload = false,
  onCancel,
  onSubmit,
}: Props) {
  const [file, setFile] = useState<File | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!visible) {
      setFile(null);
      setError(null);
    }
  }, [visible]);

  const submit = async () => {
    const validationError = validateArchiveFile(file);
    setError(validationError);
    if (validationError || !file) return;
    try {
      await onSubmit(file);
      setFile(null);
      setError(null);
    } catch (caught) {
      if (isFulfillmentOrderStaleActionError(caught)) return;
      setError(getSafeFulfillmentOrderActionErrorMessage(
        caught,
        "上传失败，请稍后重试",
      ));
    }
  };

  return (
    <Modal
      title={title}
      visible={visible}
      confirmLoading={loading}
      okText="上传归档"
      cancelText="取消"
      onCancel={onCancel}
      onOk={() => void submit()}
      unmountOnExit
    >
      <Space direction="vertical" size={12} style={{ width: "100%" }}>
        {effectiveOnUpload
          ? <Typography.Text>上传成功后协议将立即生效。</Typography.Text>
          : null}
        <Typography.Text>支持 PDF、PNG、JPEG，单个文件不超过 20 MiB。</Typography.Text>
        <label htmlFor="fulfillment-order-archive-file">盖章文件</label>
        <input
          id="fulfillment-order-archive-file"
          type="file"
          accept={FULFILLMENT_ARCHIVE_ACCEPT}
          disabled={loading}
          onChange={(event) => {
            const nextFile = event.currentTarget.files?.[0] ?? null;
            setFile(nextFile);
            setError(validateArchiveFile(nextFile));
          }}
        />
        {file ? <Typography.Text type="secondary">已选择：{file.name}</Typography.Text> : null}
        {error ? <Alert type="error" content={error} /> : null}
      </Space>
    </Modal>
  );
}
